import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

type CookieConsent = {
  necessary: boolean
  analytics: boolean
  marketing: boolean
  timestamp: string
}

export default function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [preferences, setPreferences] = useState<CookieConsent>({
    necessary: true,
    analytics: false,
    marketing: false,
    timestamp: new Date().toISOString(),
  })

  useEffect(() => {
    const consent = localStorage.getItem('dizee-cookie-consent')
    if (!consent) {
      // Show banner after a brief delay for better UX
      setTimeout(() => setIsVisible(true), 1000)
    }
  }, [])

  const saveConsent = (consent: CookieConsent) => {
    localStorage.setItem('dizee-cookie-consent', JSON.stringify(consent))
    setIsVisible(false)
  }

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    })
  }

  const acceptNecessary = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    })
  }

  const savePreferences = () => {
    saveConsent({
      ...preferences,
      necessary: true,
      timestamp: new Date().toISOString(),
    })
  }

  if (!isVisible) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998]"
        onClick={() => {
          if (!showDetails) acceptNecessary()
        }}
      />

      {/* Banner */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6 md:bottom-6 md:left-6 md:right-auto md:max-w-md"
        role="dialog"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-description"
        aria-live="polite"
      >
        <div className="ds-surface p-6 space-y-4 shadow-[0_20px_40px_rgba(0,0,0,0.3)]">
          {/* Header */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h2 id="cookie-banner-title" className="text-sm font-semibold mb-2">
                We use cookies
              </h2>
              <p id="cookie-banner-description" className="text-xs text-gray-600 dark:text-gray-400">
                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By
                clicking "Accept All", you consent to our use of cookies.{' '}
                <Link to="/privacy-policy" className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300 underline">
                  Learn more
                </Link>
              </p>
            </div>
          </div>

          {/* Details Panel */}
          {showDetails && (
            <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
              {/* Necessary Cookies */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="necessary"
                  checked={true}
                  disabled
                  className="mt-1 h-4 w-4 rounded border-gray-300 bg-gray-100 cursor-not-allowed"
                  aria-label="Necessary cookies (required)"
                />
                <div className="flex-1">
                  <label htmlFor="necessary" className="text-xs font-medium cursor-not-allowed">
                    Necessary Cookies <span className="text-gray-500">(Required)</span>
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Essential for the website to function. Cannot be disabled.
                  </p>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="analytics"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-gray-300 cursor-pointer"
                  aria-label="Analytics cookies"
                />
                <div className="flex-1">
                  <label htmlFor="analytics" className="text-xs font-medium cursor-pointer">
                    Analytics Cookies
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Help us understand how visitors interact with our website.
                  </p>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="marketing"
                  checked={preferences.marketing}
                  onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-gray-300 cursor-pointer"
                  aria-label="Marketing cookies"
                />
                <div className="flex-1">
                  <label htmlFor="marketing" className="text-xs font-medium cursor-pointer">
                    Marketing Cookies
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Used to deliver personalized advertisements.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-2 pt-2">
            {showDetails ? (
              <>
                <button onClick={savePreferences} className="ds-btn ds-btn-primary w-full text-sm">
                  Save Preferences
                </button>
                <button
                  onClick={() => setShowDetails(false)}
                  className="ds-btn ds-btn-secondary w-full text-sm"
                  aria-label="Close preferences"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button onClick={acceptAll} className="ds-btn ds-btn-primary w-full text-sm">
                  Accept All
                </button>
                <div className="flex gap-2">
                  <button onClick={acceptNecessary} className="ds-btn ds-btn-secondary flex-1 text-sm">
                    Necessary Only
                  </button>
                  <button
                    onClick={() => setShowDetails(true)}
                    className="ds-btn ds-btn-secondary flex-1 text-sm"
                    aria-label="Customize cookie preferences"
                  >
                    Customize
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Footer Links */}
          <div className="flex flex-wrap gap-4 text-xs text-gray-500 border-t border-gray-200 dark:border-gray-700 pt-4">
            <Link to="/privacy-policy" className="hover:text-gray-900 dark:hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="hover:text-gray-900 dark:hover:text-white transition-colors">
              Terms of Service
            </Link>
            <Link to="/accessibility-statement" className="hover:text-gray-900 dark:hover:text-white transition-colors">
              Accessibility
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}
